import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { ApprovalLevelRequirementEnum } from 'shared/constants';
import { useApproverUsers, useConfirmPriceEdit, useSendMarketPriceNeedsApprovalEmail } from 'shared/hooks';
import { useDistributor } from 'shared/providers';
import { DynamicEmailData } from 'shared/services/email-address-service';
import { useAuthState } from 'shared/stores';
import {
    determineApprovalLevelRequirement,
    determineApproverAndStatus,
    determineApprovers,
    determineVariance,
    formatFourDecimalMoney,
} from 'shared/utility';
import { CurrentPricesData, NeedsApprovalEmailData } from 'types/backend';
import { EditModelCurrentPricesForm } from 'types/forms';
import { EditMarketPriceForm } from './editMarketPriceForm';

interface EditMarketPriceFormProps {
    row?: CurrentPricesData;
    open: boolean;
    closeEditMarketPriceDialog: () => void;
}

export function EditMarketPriceFormDialog({ row, open, closeEditMarketPriceDialog }: EditMarketPriceFormProps) {
    const { enqueueSnackbar } = useSnackbar();
    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
        setValue,
    } = useForm<EditModelCurrentPricesForm>({
        defaultValues: {
            reason: '',
            competitor: '',
        },
    });
    const { data: approverUsers } = useApproverUsers();
    const { onSubmit } = useConfirmPriceEdit();
    const authState = useAuthState();
    const currentUser = authState.user.get()?.displayName;
    const url = window.location.origin;
    const distributor = useDistributor();

    const dynamicEmailData: Omit<DynamicEmailData, 'currentUserName' | 'currentUserEmailAddress'> = {
        territorySalesLeaderName: distributor.matchingCustomer?.territorySalesLeader || '',
        territorySalesLeaderEmail: distributor.matchingCustomer?.territorySalesLeaderEmail || '',
        territorySalesRepName: distributor.matchingCustomer?.territorySalesRep || '',
        territorySalesRepEmail: distributor.matchingCustomer?.territorySalesRepEmail || '',
    };

    const sendEmail = useSendMarketPriceNeedsApprovalEmail(`${url}/approvals`, dynamicEmailData);

    const submitMarketPriceEdit = handleSubmit(async (form: EditModelCurrentPricesForm) => {
        const submission: EditModelCurrentPricesForm = { ...row, ...form, approver: '' };

        if (row) {
            const approvalLevel = determineApprovalLevelRequirement(
                row.tierOnePrice,
                Number(form.newPrice),
                distributor.matchingCustomer?.priceTier
            );

            if (approvalLevel !== ApprovalLevelRequirementEnum.NOTREQUIRED) {
                const approvers = determineApprovers(approverUsers, approvalLevel);

                const { approver, status } = determineApproverAndStatus(
                    approvers,
                    Number(row.tierOnePrice),
                    Number(form.newPrice),
                    distributor.matchingCustomer.priceTier
                );
                submission.approver = approver;
                submission.status = status;

                const emailData: NeedsApprovalEmailData = {
                    createdBy: currentUser ?? '',
                    customerName: distributor.matchingCustomer?.customerName ?? '',
                    customerAccount: distributor.matchingCustomer?.customerNumber ?? '',
                    productId: row.productId,
                    customerTier: distributor.matchingCustomer?.priceTier ?? '',
                    territorySalesLeader: distributor.matchingCustomer?.territorySalesLeader ?? '',
                    territorySalesRep: 'Not Yet Available',
                    newPrice: formatFourDecimalMoney(Number(form.newPrice)),
                    customerTierTargetPrice: formatFourDecimalMoney(row.targetPrice),
                    tierOneTargetPrice: formatFourDecimalMoney(row.tierOnePrice),
                    currentPrice: formatFourDecimalMoney(row.price),
                    variance: determineVariance(Number(form.newPrice), Number(row.targetPrice)),
                    estAnnualVolume: form.estAnnualRevenue,
                    deviationReason: form.reason,
                    comments: form.reasonNotes ?? '',
                    approverEmails: approvers.map((a) => a.email),
                    approverLevel: approvalLevel.valueOf(),
                };

                try {
                    await sendEmail.mutateAsync(emailData);
                } catch (e) {
                    enqueueSnackbar('Market Price editted email failed to send', { variant: 'error' });
                }
            }
        }

        onSubmit(submission);
        return closeEditMarketPriceDialog();
    });

    return (
        <Dialog open={open} aria-labelledby='responsive-dialog-title'>
            <DialogTitle id='responsive-dialog-title'>{`Details for product: ${row?.productId}`}</DialogTitle>
            <DialogContent>
                <DialogContentText>Price Maintenance</DialogContentText>
                <EditMarketPriceForm row={row} control={control} errors={errors} watch={watch} setValue={setValue} />
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={closeEditMarketPriceDialog}>
                    Exit
                </Button>
                <Button onClick={submitMarketPriceEdit} autoFocus>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
}
